import { Component, computed, inject } from '@angular/core';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { SVGIcon, SVGIconModule } from '@progress/kendo-angular-icons';
import { checkCircleIcon, exclamationCircleIcon, infoCircleIcon, questionCircleIcon, xCircleIcon } from '@progress/kendo-svg-icons';
import { DialogArt, DialogService } from './dialog.service';

const DARSTELLUNG: Record<DialogArt, { icon: SVGIcon; farbe: 'primary' | 'warning' | 'error' | 'info' | 'success'; jaKlasse: string }> = {
  frage: { icon: questionCircleIcon, farbe: 'primary', jaKlasse: 'btnGruen' },
  warnung: { icon: exclamationCircleIcon, farbe: 'warning', jaKlasse: 'btnRot' },
  fehler: { icon: xCircleIcon, farbe: 'error', jaKlasse: 'btnBlau' },
  info: { icon: infoCircleIcon, farbe: 'info', jaKlasse: 'btnBlau' },
  erfolg: { icon: checkCircleIcon, farbe: 'success', jaKlasse: 'btnGruen' },
};

@Component({
  selector: 'ui-dialog-host',
  standalone: true,
  imports: [DialogsModule, ButtonsModule, SVGIconModule],
  template: `
    @if (dialog.offen(); as d) {
      <kendo-dialog [title]="d.titel" (close)="dialog.schliessen(false)" [minWidth]="250" [width]="450" [class]="'ui-dialog-' + d.art">
        <div class="ui-dialog-inhalt">
          <kendo-svgicon [icon]="darstellung()!.icon" [themeColor]="darstellung()!.farbe" size="large"></kendo-svgicon>
          <div class="ui-dialog-text">{{ d.text }}</div>
        </div>

        <kendo-dialog-actions layout="stretched">
          <button kendoButton [class]="darstellung()!.jaKlasse" (click)="dialog.schliessen(true)">{{ d.jaText }}</button>
          @if (d.neinText) {
            <button kendoButton class="btnBlau" (click)="dialog.schliessen(false)">{{ d.neinText }}</button>
          }
        </kendo-dialog-actions>
      </kendo-dialog>
    }
  `,
  styles: `
    .ui-dialog-inhalt {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      margin: 16px 8px;
    }
    .ui-dialog-text {
      white-space: pre-wrap; /* Zeilenumbrüche im Text (\\n) werden angezeigt */
      font-weight: 500;
      line-height: 1.5;
      padding-top: 2px; /* Text auf Höhe des Icons */
    }

    /* Farbiger Streifen oben je Art */
    :host ::ng-deep kendo-dialog .k-dialog {
      border-top: 4px solid transparent;
    }
    :host ::ng-deep kendo-dialog.ui-dialog-frage .k-dialog {
      border-top-color: #0d6efd;
    }
    :host ::ng-deep kendo-dialog.ui-dialog-warnung .k-dialog {
      border-top-color: #ffc107;
    }
    :host ::ng-deep kendo-dialog.ui-dialog-fehler .k-dialog {
      border-top-color: #dc3545;
    }
    :host ::ng-deep kendo-dialog.ui-dialog-info .k-dialog {
      border-top-color: #0dcaf0;
    }
    :host ::ng-deep kendo-dialog.ui-dialog-erfolg .k-dialog {
      border-top-color: #198754;
    }
    /* Keine Trennlinie über den Buttons */
    :host ::ng-deep kendo-dialog .k-dialog-actions,
    :host ::ng-deep kendo-dialog .k-actions {
      border-top: none;
    }
  `,
})
export class UiDialogHostComponent {
  protected dialog = inject(DialogService);
  protected darstellung = computed(() => {
    const d = this.dialog.offen();
    return d ? DARSTELLUNG[d.art] : null;
  });
}
